import { Fragment, ReactNode, useEffect, useMemo, useRef } from "react"
import { useDrag, useDrop } from "react-dnd"
import { useTranslation } from "react-i18next"
import { Move, Plus, Rows2, X } from "lucide-react"
import { useFeatureFlagEnabled } from "posthog-js/react"
import { Chat } from "@/reducers/chatsSlice"
import { Message } from "@/reducers/messagesSlice"
import { MessageOptions } from "@/reducers/chatsSliceThunks"
import { cn } from "@/lib/utils"
import {
  ALL_WIDGET_TABS,
  BuiltinWidgetTabId,
  getTaskDetailsEntry,
  isTaskDetailsId,
  useWidgetTabs,
  WIDGET_TAB_LABEL_KEYS,
  WidgetTabId,
  WidgetWindow,
  WindowIndex,
} from "@/hooks/useWidgetTabs"
import TaskDetailsTool from "@/components/chat/TaskDetailsTool"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import DriverChatCard from "@/components/chat/DriverChatCard"
import AutomationsCard from "@/components/slickAI/AutomationsCard"
import MapWidgetBody from "@/components/chat/MapWidgetBody"
import WorkTimeWidgetBody from "@/components/chat/WorkTimeWidgetBody"
import TemperatureWidgetBody from "@/components/chat/TemperatureWidgetBody"
import { selectChatAutomationScope } from "@/reducers/accountChatsSelectors"
import { useAppSelector } from "@/reducers/hooks"

type Props = {
  chat: Chat
  driverJid: string
  driverVisibleMessages: Message[]
  onSend: (text: string, files: FileList, options?: MessageOptions) => void
  onAddAutomation: (prompt: string) => void
}

const NOOP = () => {}

const CARD =
  "flex min-h-0 flex-col overflow-hidden rounded-lg border border-border bg-card shadow-[0_4px_20px_rgba(0,0,0,0.04)]"

/** react-dnd item type for a widget tab pill being dragged. */
const TAB_DND = "widget-tab-pill"
type TabDragItem = { id: WidgetTabId }

const DraggableTab = ({
  id,
  label,
  active,
  closable,
  onSelect,
  onClose,
}: {
  id: WidgetTabId
  label: string
  active: boolean
  closable: boolean
  onSelect: () => void
  onClose: () => void
}) => {
  const { t } = useTranslation()
  const ref = useRef<HTMLDivElement>(null)
  const [{ dragging }, drag] = useDrag(
    () => ({
      type: TAB_DND,
      item: { id } as TabDragItem,
      collect: (m) => ({ dragging: m.isDragging() }),
    }),
    [id],
  )
  drag(ref)

  // A crowded strip scrolls; a tab that becomes active (clicked, or opened
  // from elsewhere) scrolls into view instead of landing out of sight.
  useEffect(() => {
    if (active)
      ref.current?.scrollIntoView({ block: "nearest", inline: "nearest" })
  }, [active])

  return (
    <div
      ref={ref}
      className={cn(
        "group flex cursor-grab items-center gap-1 rounded-md py-1 pl-2.5 text-sm font-medium transition-colors active:cursor-grabbing",
        // Floor = pl-2.5 + the label's min-w-[2rem] + gap-1 + the × (p-0.5,
        // h-3.5) + pr-1 — narrower, and the × spills under the next tab.
        // Recount when any of those change.
        closable ? "min-w-[4.25rem] pr-1" : "min-w-[3.25rem] pr-2.5",
        // A crowded strip narrows the other tabs; the open one stays whole.
        active
          ? "shrink-0 bg-muted text-foreground"
          : "shrink text-muted-foreground hover:bg-muted/60",
        dragging && "opacity-40",
      )}
    >
      <button
        type="button"
        data-testid={`widget-tab-${id}`}
        data-state={active ? "active" : "inactive"}
        className="min-w-[2rem] max-w-[12rem] truncate"
        onClick={onSelect}
        title={label}
      >
        {label}
      </button>
      {closable && (
        <button
          type="button"
          aria-label={t("chat.widgetTabs.closeTab", { label })}
          className="shrink-0 rounded p-0.5 text-muted-foreground transition-colors hover:bg-black/10 dark:hover:bg-white/10"
          onClick={onClose}
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  )
}

/** The `h-11` tab bar for one window. */
const WindowTabStrip = ({
  active,
  tabs,
  addable,
  closable,
  label,
  onSelect,
  onClose,
  onCloseWindow,
  onAddTab,
}: {
  active: WidgetTabId
  /** Only this window's tabs. */
  tabs: WidgetTabId[]
  /** Widgets this window's "+" can open. */
  addable: BuiltinWidgetTabId[]
  /** Whether the per-tab × control shows (false when it's the last widget). */
  closable: boolean
  label: (id: WidgetTabId) => string
  onSelect: (id: WidgetTabId) => void
  onClose: (id: WidgetTabId) => void
  /** Window-level × on the right: closes this window, or hides the panel
   * when it is the only one. */
  onCloseWindow: () => void
  /** The per-window "+" menu adds a widget to this window. */
  onAddTab: (id: WidgetTabId) => void
}) => {
  const { t } = useTranslation()
  return (
    <div className="flex h-11 shrink-0 items-center gap-1 border-b border-border px-2">
      <div className="flex min-w-0 flex-1 items-center gap-1 overflow-x-auto scrollbar-borderless">
        {tabs.map((id, i) => (
          <Fragment key={id}>
            {i > 0 && (
              // The active pill has its own filled background; a divider
              // touching it would read as part of the pill.
              <div
                className={cn(
                  "h-5 w-px shrink-0 bg-border",
                  (id === active || tabs[i - 1] === active) && "invisible",
                )}
              />
            )}
            <DraggableTab
              id={id}
              label={label(id)}
              active={id === active}
              closable={closable}
              onSelect={() => onSelect(id)}
              onClose={() => onClose(id)}
            />
          </Fragment>
        ))}
      </div>
      {addable.length > 0 && (
        <>
          <div className="h-5 w-px shrink-0 bg-border" />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                aria-label={t("chat.widgetTabs.addToWindow")}
                title={t("chat.widgetTabs.addWidget")}
                className="shrink-0 rounded p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              >
                <Plus className="h-4 w-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-40">
              {addable.map((id) => (
                <DropdownMenuItem key={id} onClick={() => onAddTab(id)}>
                  {t(WIDGET_TAB_LABEL_KEYS[id])}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </>
      )}
      <button
        type="button"
        aria-label={t("chat.widgetTabs.closeWindow")}
        title={t("chat.widgetTabs.closeWindow")}
        className="shrink-0 rounded p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        onClick={onCloseWindow}
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  )
}

/** What dropping a tab on the zone does: open it in a new window above or
 * below a single window, or move it into this window. */
type DropArea = "above" | "below" | "into"

/**
 * Drop a tab here to reshape the panel. What a drop does is the caller's
 * call — the zone only knows where it sits and which tabs it turns away
 * (`holds`: the ones already in its window).
 */
const DropZone = ({
  area,
  holds = [],
  onDropTab,
}: {
  area: DropArea
  holds?: WidgetTabId[]
  onDropTab: (id: WidgetTabId) => void
}) => {
  const { t } = useTranslation()
  const ref = useRef<HTMLDivElement>(null)
  const [{ over, canDrop }, drop] = useDrop(
    () => ({
      accept: TAB_DND,
      canDrop: (item: TabDragItem) => !holds.includes(item.id),
      drop: (item: TabDragItem) => onDropTab(item.id),
      collect: (m) => ({ over: m.isOver(), canDrop: m.canDrop() }),
    }),
    [holds, onDropTab],
  )
  drop(ref)

  return (
    <div
      ref={ref}
      data-drop-area={area}
      className={cn(
        "absolute z-20",
        area === "above" && "inset-x-0 top-0 h-1/2",
        area === "below" && "inset-x-0 bottom-0 h-1/2",
        area === "into" && "inset-0",
        canDrop ? "pointer-events-auto" : "pointer-events-none",
      )}
    >
      {canDrop && (
        <div
          className={cn(
            "m-2 flex h-[calc(100%-1rem)] items-center justify-center gap-2 rounded-lg border-2 border-dashed text-xs font-medium transition-colors",
            over
              ? "border-primary bg-primary/10 text-foreground"
              : "border-border bg-muted/50 text-muted-foreground",
          )}
        >
          {area === "into" ? (
            <>
              <Move className="h-4 w-4" />
              {t("chat.widgetTabs.moveHere")}
            </>
          ) : (
            <>
              <Rows2 className="h-4 w-4" />
              {t("chat.widgetTabs.splitPanel")}
            </>
          )}
        </div>
      )}
    </div>
  )
}

const WidgetTabs = ({
  chat,
  driverJid,
  driverVisibleMessages,
  onSend,
  onAddAutomation,
}: Props) => {
  const { t } = useTranslation()
  const {
    openTabs,
    windows,
    setActiveTab,
    closeWindow,
    hidePanel,
    removeTab,
    addTabToWindow,
    moveTab,
    splitWindow,
    showRoom,
  } = useWidgetTabs()

  // The panel belongs to one room, so its automations tab answers for that
  // room only — the driver in it and the truck they drive.
  const automationScope = useAppSelector(
    useMemo(() => selectChatAutomationScope(chat.jid), [chat.jid]),
  )

  // A task-details tab is a snapshot opened from *this* room's messages
  // (D2x) — it must not linger once the dispatcher moves on to another
  // driver's chat, or the panel keeps showing a stranger's order.
  useEffect(() => {
    showRoom(chat.jid)
  }, [chat.jid, showRoom])

  const temperatureAlerts = Boolean(useFeatureFlagEnabled("temperature_alerts"))
  // One of each widget: "+" only offers what isn't open in any window.
  const addable = ALL_WIDGET_TABS.filter(
    (id) =>
      (id !== "temperature" || temperatureAlerts) && !openTabs.includes(id),
  )

  const closable = openTabs.length > 1
  // With a single widget open there is nothing to move into a new window.
  const canGrow = openTabs.length > 1

  const tabLabel = (id: WidgetTabId): string =>
    isTaskDetailsId(id)
      ? (getTaskDetailsEntry(id)?.label ?? t("chat.widgetTabs.taskFallback"))
      : t(WIDGET_TAB_LABEL_KEYS[id])

  const builtinBodies: Record<BuiltinWidgetTabId, () => ReactNode> = {
    chat: () => (
      <DriverChatCard
        bare
        chat={chat}
        messages={driverVisibleMessages}
        onSend={onSend}
      />
    ),
    automations: () => (
      <AutomationsCard
        bare
        expanded
        onExpandedChange={NOOP}
        onAddAutomation={onAddAutomation}
        scope={automationScope}
      />
    ),
    map: () => <MapWidgetBody chat={chat} driverJid={driverJid} />,
    workTime: () => <WorkTimeWidgetBody chat={chat} />,
    temperature: () => <TemperatureWidgetBody driverJid={driverJid} />,
  }

  const widgetBody = (id: WidgetTabId): ReactNode => {
    if (!isTaskDetailsId(id)) return builtinBodies[id]()
    const entry = getTaskDetailsEntry(id)
    return entry ? <TaskDetailsTool detail={entry.detail} /> : null
  }

  const [top, bottom] = windows
  const split = bottom !== undefined

  /**
   * One window: tab strip plus content, with every tab of that window kept
   * mounted and hidden by CSS. Switching tabs or reshaping the panel then
   * never remounts a widget, so the map does not reload.
   */
  const renderWindow = (index: WindowIndex, { tabs, active }: WidgetWindow) => (
    <div className={cn(CARD, "relative flex-1")}>
      <WindowTabStrip
        active={active}
        tabs={tabs}
        addable={addable}
        closable={closable}
        label={tabLabel}
        onSelect={(id) => setActiveTab(index, id)}
        onClose={removeTab}
        // The last window standing has nowhere to fold into, so its × hides
        // the panel instead.
        onCloseWindow={split ? () => closeWindow(index) : hidePanel}
        onAddTab={(id) => addTabToWindow(index, id)}
      />
      <div className="relative min-h-0 flex-1">
        {tabs.map((id) => (
          <div
            key={id}
            className={cn("absolute inset-0", id !== active && "hidden")}
          >
            {widgetBody(id)}
          </div>
        ))}
        {/* Over the content only: letting go of a tab back on its own strip
            must not split the window. */}
        {!split && canGrow && (
          <>
            <DropZone
              area="above"
              onDropTab={(id) => splitWindow("above", id)}
            />
            <DropZone
              area="below"
              onDropTab={(id) => splitWindow("below", id)}
            />
          </>
        )}
      </div>
      {split && (
        <DropZone
          area="into"
          holds={tabs}
          onDropTab={(id) => moveTab(index, id)}
        />
      )}
    </div>
  )

  return (
    <div className="flex h-full w-full min-w-0 flex-col gap-2">
      {renderWindow(0, top)}
      {bottom && renderWindow(1, bottom)}
    </div>
  )
}

export default WidgetTabs
